import { useCallback, useRef } from "react";
import { Fab } from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";

interface Props {
  bottom: number;
  right: number;
  onClick: () => void;
  onDrag: (bottom: number, right: number) => void;
}

const DRAG_THRESHOLD = 5;

const ArchieButton = ({ bottom, right, onClick, onDrag }: Readonly<Props>) => {
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startBottom: number;
    startRight: number;
    moved: boolean;
  } | null>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startBottom: bottom,
        startRight: right,
        moved: false,
      };
    },
    [bottom, right]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;

      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;

      if (!drag.moved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
        return;
      }
      drag.moved = true;

      onDrag(drag.startBottom - dy, drag.startRight - dx);
    },
    [onDrag]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      e.currentTarget.releasePointerCapture(e.pointerId);
      const drag = dragRef.current;
      dragRef.current = null;

      if (!drag?.moved) {
        onClick();
      }
    },
    [onClick]
  );

  return (
    <Fab
      color="primary"
      aria-label="Open Archie chat"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      sx={{
        position: "fixed",
        bottom,
        right,
        zIndex: 1100,
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
        touchAction: "none",
      }}
    >
      <ChatIcon />
    </Fab>
  );
};

export default ArchieButton;
